import type { KataId } from '../../data/catalog/types'
import { getDb, PREFS_KEY } from '../db'
import type { AppPreferences, TechniqueSegment, VideoReference } from '../schema'

function nowIso(): string {
  return new Date().toISOString()
}

function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `ref-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export async function listReferencesForKata(kataId: KataId): Promise<VideoReference[]> {
  const db = await getDb()
  return db.getAllFromIndex('references', 'by-kata', kataId)
}

export async function getActiveReference(kataId: KataId): Promise<VideoReference | undefined> {
  const refs = await listReferencesForKata(kataId)
  // M1: one reference per kata is enough; pick most recently updated.
  return refs.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]
}

export async function getReferenceById(id: string): Promise<VideoReference | undefined> {
  const db = await getDb()
  return db.get('references', id)
}

export type SaveReferenceInput = {
  id?: string
  kataId: KataId
  title: string
  videoId: string
  segments?: TechniqueSegment[]
  /** When replacing video identity, pass [] so old timings are not reused. */
  clearSegments?: boolean
}

/**
 * Persist a reference. Returns the saved record only after IndexedDB write succeeds.
 * Changing videoId without an explicit id reuse clears segments unless caller copies them.
 */
export async function saveReference(input: SaveReferenceInput): Promise<VideoReference> {
  const db = await getDb()
  const existing = input.id ? await db.get('references', input.id) : undefined
  const timestamp = nowIso()

  const videoChanged = existing && existing.videoId !== input.videoId
  let segments: TechniqueSegment[]
  if (input.clearSegments || videoChanged) {
    segments = input.segments ?? []
  } else if (input.segments !== undefined) {
    segments = input.segments
  } else {
    segments = existing?.segments ?? []
  }

  // If video identity changed and caller did not supply new segments, force empty.
  if (videoChanged && input.segments === undefined && !input.clearSegments) {
    segments = []
  }

  const record: VideoReference = {
    id: existing?.id ?? input.id ?? createId(),
    kataId: input.kataId,
    title: input.title.trim() || 'YouTube reference',
    provider: 'youtube',
    videoId: input.videoId,
    segments,
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
  }

  await db.put('references', record)
  return record
}

export async function updateSegments(
  referenceId: string,
  segments: TechniqueSegment[],
): Promise<VideoReference> {
  const db = await getDb()
  const existing = await db.get('references', referenceId)
  if (!existing) {
    throw new Error('Reference not found. Save could not complete.')
  }
  const record: VideoReference = {
    ...existing,
    segments,
    updatedAt: nowIso(),
  }
  await db.put('references', record)
  return record
}

/**
 * Upsert a single technique segment on a reference.
 * Other techniques' segments are preserved.
 */
export async function upsertTechniqueSegment(
  referenceId: string,
  segment: TechniqueSegment,
): Promise<VideoReference> {
  const db = await getDb()
  const existing = await db.get('references', referenceId)
  if (!existing) {
    throw new Error('Reference not found. Save could not complete.')
  }
  const others = existing.segments.filter((s) => s.techniqueId !== segment.techniqueId)
  const record: VideoReference = {
    ...existing,
    segments: [...others, segment],
    updatedAt: nowIso(),
  }
  await db.put('references', record)
  return record
}

/** Remove a technique segment. Catalog techniques are never deleted. */
export async function clearTechniqueSegment(
  referenceId: string,
  techniqueId: string,
): Promise<VideoReference> {
  const db = await getDb()
  const existing = await db.get('references', referenceId)
  if (!existing) {
    throw new Error('Reference not found. Clear could not complete.')
  }
  const record: VideoReference = {
    ...existing,
    segments: existing.segments.filter((s) => s.techniqueId !== techniqueId),
    updatedAt: nowIso(),
  }
  await db.put('references', record)
  return record
}

/**
 * Remove a personal video reference after caller confirmation.
 * Never deletes catalog data.
 */
export async function removeReference(id: string): Promise<void> {
  const db = await getDb()
  const existing = await db.get('references', id)
  if (!existing) {
    throw new Error('Reference not found. Nothing was removed.')
  }
  await db.delete('references', id)
}

export async function getPreferences(): Promise<AppPreferences> {
  const db = await getDb()
  const row = await db.get('preferences', PREFS_KEY)
  if (!row) return {}
  const { id, ...prefs } = row
  void id
  return prefs
}

/** Merge-patch preferences so callers never wipe unrelated keys. */
export async function savePreferences(patch: AppPreferences): Promise<void> {
  const db = await getDb()
  const existing = await getPreferences()
  await db.put('preferences', { id: PREFS_KEY, ...existing, ...patch })
}

/** Data-saver deferred YouTube load — on unless explicitly disabled. */
export function isYoutubeDataSaverEnabled(prefs: AppPreferences): boolean {
  return prefs.deferYoutubeLoad !== false
}
